import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, fetchHoleScoresPaginated, fetchPaginated } from '../../lib/supabase';
import { computeLeaderboard, sortLeaderboard, type ScoringMethod } from '../../lib/scoring';

export interface TvHoleScore {
  round: number;
  hole: number;
  strokes: number;
  par: number | null;
  score_status?: string;
}

export interface TvLeaderboardEntry {
  id: string;
  tournament_id: string;
  player_name: string;
  country: string;
  resident_club: string;
  category: string;
  handicap: number;
  total_score: number;
  round_scores: number[];
  rounds_played: number;
  current_round: number | null;
  current_hole: number | null;
  position: number;
  overall_status?: 'PLAYED' | 'NO_SCORE' | 'DNP' | 'WD' | 'DQ';
  hole_scores?: TvHoleScore[];
  drawn_rounds?: number[];
}

export interface TvTournament {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  status: string;
  venue: string | null;
  prize_pool: number;
  scoring_method?: string;
}

export interface TvEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  type: string;
  description: string | null;
}

export interface TvPartner {
  id: string;
  company_name: string;
  logo_url: string | null;
  tier: string;
  description: string | null;
  image_url: string | null;
  website: string | null;
  contribution: number | null;
  stats: string[] | null;
}

export interface TvPhoto {
  id: string;
  url: string;
  caption: string | null;
  gallery_name: string;
}

export interface TvPairingPlayer {
  id: string;
  first_name: string;
  last_name: string;
  category: string | null;
  handicap: number | null;
  resident_club: string | null;
  country: string | null;
}

export interface TvPairing {
  id: string;
  group_number: number;
  sort_order: number;
  tee_time: string | null;
  player: TvPairingPlayer | null;
}

export interface TvPairingGroup {
  group_number: number;
  tee_time: string | null;
  starting_hole: number | null;
  pairings: TvPairing[];
}

export interface TvRound {
  id: string;
  name: string;
  round_number: number;
  date: string | null;
  groups: TvPairingGroup[];
}

export interface TvSpecialPrize {
  id: string;
  tournament_id: string;
  round_number: number;
  prize_type: 'nearest_pin' | 'longest_drive' | 'hole_in_one';
  gender: 'men' | 'ladies';
  hole_number: number | null;
  winner_name: string | null;
  distance: string | null;
}

export interface TvData {
  tournament: TvTournament | null;
  allTournaments: TvTournament[];
  leaderboard: TvLeaderboardEntry[];
  events: TvEvent[];
  partners: TvPartner[];
  photos: TvPhoto[];
  rounds: TvRound[];
  specialPrizes: TvSpecialPrize[];
  orgLogoUrl: string | null;
  loading: boolean;
  lastUpdated: Date | null;
}

export function useTvData(selectedTournamentId?: string | null): TvData {
  const [tournament, setTournament] = useState<TvTournament | null>(null);
  const [allTournaments, setAllTournaments] = useState<TvTournament[]>([]);
  const [leaderboard, setLeaderboard] = useState<TvLeaderboardEntry[]>([]);
  const [events, setEvents] = useState<TvEvent[]>([]);
  const [partners, setPartners] = useState<TvPartner[]>([]);
  const [photos, setPhotos] = useState<TvPhoto[]>([]);
  const [rounds, setRounds] = useState<TvRound[]>([]);
  const [specialPrizes, setSpecialPrizes] = useState<TvSpecialPrize[]>([]);
  const [orgLogoUrl, setOrgLogoUrl] = useState<string | null>(null);
  const [holeParMap, setHoleParMap] = useState<Record<number, number>>({});
  const [holeHcpMap, setHoleHcpMap] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const courseDataRef = useRef<{ tournamentId: string; holeParMap: Record<number, number>; holeHcpMap: Record<number, number> } | null>(null);
  const fetchingRef = useRef(false);

  const prevTournamentRef = useRef<string | null | undefined>(selectedTournamentId);
  useEffect(() => {
    if (prevTournamentRef.current != null && prevTournamentRef.current !== selectedTournamentId) {
      setLeaderboard([]);
      setEvents([]);
      setPartners([]);
      setPhotos([]);
      setRounds([]);
    }
    prevTournamentRef.current = selectedTournamentId;
  }, [selectedTournamentId]);

  const fetchAll = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const { data: fetchedTournaments } = await supabase
        .from('tournaments')
        .select('id, name, start_date, end_date, status, venue, prize_pool')
        .in('status', ['ongoing', 'active', 'upcoming', 'completed'])
        .order('start_date', { ascending: false });

      if (!fetchedTournaments || fetchedTournaments.length === 0) {
        setLoading(false);
        return;
      }

      setAllTournaments(fetchedTournaments);

      let tournamentData: TvTournament;

      if (selectedTournamentId) {
        tournamentData = fetchedTournaments.find((t) => t.id === selectedTournamentId) ?? fetchedTournaments[0];
      } else {
        // Default to the most recent tournament (sorted by start_date desc)
        tournamentData = fetchedTournaments[0];
      }

      setTournament(tournamentData);

      const { data: smData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', `scoring_method_${tournamentData.id}`)
        .maybeSingle();
      if (smData?.value) {
        tournamentData = { ...tournamentData, scoring_method: smData.value as string };
        setTournament(tournamentData);
      }

      const logoRes = await supabase
        .from('logos')
        .select('url')
        .eq('type', 'primary')
        .eq('is_active', true)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (logoRes.data?.url) setOrgLogoUrl(logoRes.data.url);

      const [leaderboardRes, eventsRes, partnersRes, galleriesRes] = await Promise.all([
        supabase
          .from('leaderboard_view')
          .select('*')
          .eq('tournament_id', tournamentData.id),
        supabase
          .from('events')
          .select('*')
          .eq('tournament_id', tournamentData.id)
          .order('date', { ascending: true }),
        supabase
          .from('partners')
          .select('id, company_name, logo_url, tier, description, image_url, website, contribution, stats')
          .eq('status', 'active')
          .eq('tournament_id', tournamentData.id)
          .order('contribution', { ascending: false }),
        supabase
          .from('galleries')
          .select('id, name')
          .eq('tournament_id', tournamentData.id),
      ]);

      if (leaderboardRes.data) {
        // Build a map of player_id -> round numbers they are drawn in
        const drawnRoundsMap: Record<string, number[]> = {};
        const { data: roundRowsForPairings } = await supabase
          .from('tournament_rounds')
          .select('*')
          .eq('tournament_id', tournamentData.id);

        const { data: disabledRoundsSetting } = await supabase
          .from('system_settings')
          .select('value')
          .eq('key', `disabled_rounds_${tournamentData.id}`)
          .maybeSingle();
        const disabledArr: number[] = Array.isArray(disabledRoundsSetting?.value) ? disabledRoundsSetting.value as number[] : [];
        const disabledSet = new Set(disabledArr);

        const enabledRoundNumbers = new Set<number>(
          (roundRowsForPairings ?? []).filter(r => !disabledSet.has(r.round_number)).map(r => r.round_number)
        );

        if (roundRowsForPairings && roundRowsForPairings.length > 0) {
          const roundIdToNumber: Record<string, number> = {};
          for (const r of roundRowsForPairings) {
            roundIdToNumber[r.id] = r.round_number;
          }
          const pairingData = await fetchPaginated<{ player_id: string; round_id: string }>(
            'player_pairings',
            'player_id, round_id',
            [{ column: 'round_id', op: 'in', value: roundRowsForPairings.map((r) => r.id) }],
          );

          if (pairingData) {
            for (const p of pairingData) {
              const roundNum = roundIdToNumber[p.round_id];
              if (roundNum != null) {
                if (!drawnRoundsMap[p.player_id]) drawnRoundsMap[p.player_id] = [];
                drawnRoundsMap[p.player_id].push(roundNum);
              }
            }
          }
        }

        // Use cached course hole data or fetch it
        let holeParMap: Record<number, number> = {};
        let holeHcpMap: Record<number, number> = {};
        if (courseDataRef.current && courseDataRef.current.tournamentId === tournamentData.id) {
          holeParMap = courseDataRef.current.holeParMap;
          holeHcpMap = courseDataRef.current.holeHcpMap;
        } else {
          const { data: tournamentCourse } = await supabase
            .from('tournament_courses')
            .select('course_id')
            .eq('tournament_id', tournamentData.id)
            .maybeSingle();

          if (tournamentCourse?.course_id) {
            const { data: holesData } = await supabase
              .from('holes')
              .select('hole_number, par, handicap_index')
              .eq('course_id', tournamentCourse.course_id)
              .order('hole_number');
            if (holesData) {
              for (const h of holesData) {
                holeParMap[h.hole_number] = h.par;
                holeHcpMap[h.hole_number] = h.handicap_index ?? h.hole_number;
              }
            }
          }
          courseDataRef.current = { tournamentId: tournamentData.id, holeParMap, holeHcpMap };
        }
        setHoleParMap(holeParMap);
        setHoleHcpMap(holeHcpMap);

        // Fetch hole_scores for all players to compute live totals
        const allPlayerIds = leaderboardRes.data.map((e) => e.id);
        let holeScoreMap: Record<string, TvHoleScore[]> = {};

        if (allPlayerIds.length > 0) {
          const holeRows = await fetchHoleScoresPaginated(tournamentData.id, allPlayerIds);

          if (holeRows.length > 0) {
            for (const row of holeRows) {
              if (!holeScoreMap[row.player_id]) holeScoreMap[row.player_id] = [];
              holeScoreMap[row.player_id].push({ round: row.round, hole: row.hole, strokes: row.strokes, par: holeParMap[row.hole] ?? 4, score_status: row.score_status ?? undefined });
            }
          }
        }

        // For each player, compute net score from hole_scores using the shared scoring function.
        const method: ScoringMethod = (tournamentData.scoring_method as ScoringMethod) || 'stroke_play_net';

        const enabledRoundsSet: Set<number> | undefined = enabledRoundNumbers.size > 0 ? enabledRoundNumbers : undefined;

        const entriesForScoring = leaderboardRes.data
          .filter(e => e.overall_status !== 'WD')
          .map(e => ({ id: e.id, handicap: Math.floor(e.handicap ?? 0) }));

        const holeScoreMapForScoring: Record<string, { round: number; hole: number; strokes: number; score_status?: string }[]> = {};
        for (const [pid, holes] of Object.entries(holeScoreMap)) {
          holeScoreMapForScoring[pid] = holes.map(h => ({ round: h.round, hole: h.hole, strokes: h.strokes, score_status: h.score_status }));
        }

        const computed = computeLeaderboard(
          entriesForScoring,
          holeScoreMapForScoring,
          holeParMap,
          holeHcpMap,
          method,
          enabledRoundsSet,
        );
        const computedMap = new Map(computed.map(c => [c.id, c]));

        const withStatus = leaderboardRes.data.filter(e => e.overall_status !== 'WD').map((entry) => {
          const c = computedMap.get(entry.id);
          const computedTotal = c && c.rounds_played > 0 ? c.total_score : entry.total_score as number;
          const computedRoundsPlayed = c && c.rounds_played > 0 ? c.rounds_played : entry.rounds_played as number;
          const computedRoundScores = c && c.rounds_played > 0 ? c.round_scores : entry.round_scores as number[] | null;

          return {
            ...entry,
            total_score: computedTotal,
            rounds_played: computedRoundsPlayed,
            round_scores: computedRoundScores,
            overall_status: (entry.overall_status ||
              (computedRoundsPlayed > 0 ? 'PLAYED' : 'NO_SCORE')) as TvLeaderboardEntry['overall_status'],
          };
        });

        const sorted = sortLeaderboard(withStatus, method);

        setLeaderboard(sorted.map((e) => ({
          ...e,
          hole_scores: holeScoreMap[e.id] ?? [],
          drawn_rounds: drawnRoundsMap[e.id] ?? [],
        })));
      }

      if (eventsRes.data) setEvents(eventsRes.data);
      if (partnersRes.data) setPartners(partnersRes.data);

      const { data: contentSection } = await supabase
        .from('content_sections')
        .select('id')
        .eq('page', 'history')
        .eq('title', 'Gallery')
        .maybeSingle();

      if (contentSection) {
        let galleryQuery = supabase
          .from('content_items')
          .select('id, title, content, tournament_id')
          .eq('section_id', contentSection.id)
          .eq('status', 'Published')
          .eq('type', 'gallery')
          .eq('tournament_id', tournamentData.id);

        const { data: galleryItems } = await galleryQuery;

        if (galleryItems && galleryItems.length > 0) {
          const allPhotos: TvPhoto[] = [];
          for (const item of galleryItems) {
            try {
              let parsed = item.content;
              let attempts = 0;
              while (typeof parsed === 'string' && attempts < 5) {
                parsed = JSON.parse(parsed);
                attempts++;
              }
              if (Array.isArray(parsed)) {
                for (const category of parsed) {
                  if (category.photos && Array.isArray(category.photos)) {
                    for (const photo of category.photos) {
                      if (photo && photo.url) {
                        allPhotos.push({
                          id: photo.id || `${item.id}-${allPhotos.length}`,
                          url: photo.url,
                          caption: photo.caption || null,
                          gallery_name: item.title,
                        });
                      }
                    }
                  }
                }
              }
            } catch {}
            if (allPhotos.length >= 60) break;
          }
          setPhotos(allPhotos.slice(0, 60));
        }
      } else if (galleriesRes.data && galleriesRes.data.length > 0) {
        const galleryIds = galleriesRes.data.map((g) => g.id);
        const { data: photosData } = await supabase
          .from('photos')
          .select('id, url, caption, gallery_id')
          .in('gallery_id', galleryIds)
          .limit(60);

        if (photosData) {
          const galleryMap = Object.fromEntries(
            galleriesRes.data.map((g) => [g.id, g.name])
          );
          setPhotos(
            photosData.map((p) => ({
              ...p,
              gallery_name: galleryMap[p.gallery_id ?? ''] ?? '',
            }))
          );
        }
      }

      const { data: roundRows, error: roundsError } = await supabase
        .from('tournament_rounds')
        .select('id, name, round_number, date')
        .eq('tournament_id', tournamentData.id)
        .order('round_number');

      if (!roundsError && roundRows && roundRows.length > 0) {
        const roundIds = roundRows.map((r) => r.id);
        const pairingRows = await fetchPaginated<any>(
          'player_pairings',
          '*',
          [{ column: 'round_id', op: 'in', value: roundIds }],
        );

        const playerIds = [...new Set(pairingRows.map((p: any) => p.player_id))];
        const playerMap = new Map<string, TvPairingPlayer>();

        if (playerIds.length > 0) {
          const playerRows = await fetchPaginated<TvPairingPlayer>(
            'players',
            'id, first_name, last_name, category, handicap, resident_club, country',
            [{ column: 'id', op: 'in', value: playerIds }],
          );
          playerRows.forEach((p) => playerMap.set(p.id, p));
        }

        const builtRounds: TvRound[] = roundRows.map((r) => {
          const rPairings = pairingRows.filter((p: any) => p.round_id === r.id);
          const groupMap = new Map<number, TvPairingGroup>();
          for (const p of rPairings) {
            if (!groupMap.has(p.group_number)) {
              groupMap.set(p.group_number, { group_number: p.group_number, tee_time: p.tee_time, starting_hole: (p as any).starting_hole ?? 1, pairings: [] });
            }
            const grp = groupMap.get(p.group_number)!;
            if (p.tee_time) grp.tee_time = p.tee_time;
            grp.pairings.push({ id: p.id, group_number: p.group_number, sort_order: p.sort_order, tee_time: p.tee_time, player: playerMap.get(p.player_id) ?? null });
          }
          return {
            id: r.id,
            name: r.name,
            round_number: r.round_number,
            date: r.date,
            groups: Array.from(groupMap.values()).sort((a, b) => a.group_number - b.group_number),
          };
        });

        setRounds(builtRounds);
      }

      // Fetch special prizes
      try {
        const { data: prizesData } = await supabase
          .from('special_prizes')
          .select('id, tournament_id, round_number, prize_type, gender, hole_number, winner_name, distance')
          .eq('tournament_id', tournamentData.id)
          .order('round_number');
        if (prizesData) setSpecialPrizes(prizesData);
      } catch {
        // Table may not exist yet
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('TV data fetch error:', err);
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  }, [selectedTournamentId]);

  useEffect(() => {
    fetchAll();

    const debouncedFetch = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(fetchAll, 500);
    };

    // Poll every 5 seconds for near real-time updates
    const pollInterval = setInterval(fetchAll, 5000);

    const channelName = `tv-realtime-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'hole_scores' }, debouncedFetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'player_scores' }, debouncedFetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'players' }, debouncedFetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'player_pairings' }, debouncedFetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tournaments' }, debouncedFetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'events' }, debouncedFetch)
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('TV realtime channel connected');
        } else if (status === 'CHANNEL_ERROR') {
          console.warn('TV realtime channel error - relying on polling');
        }
      });

    return () => {
      clearInterval(pollInterval);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      supabase.removeChannel(channel);
    };
  }, [fetchAll]);

  return { tournament, allTournaments, leaderboard, events, partners, photos, rounds, specialPrizes, orgLogoUrl, holeParMap, holeHcpMap, loading, lastUpdated };
}
